import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';
import { Permission } from '../../permissions/roles.js';
import { Person } from '../../users/entities/person.js';
import { EmploymentPermitsAPI } from '../EmploymentPermitsAPI.js';

function getStatus(
  person: NarrowedEntity<
    Person,
    {
      mitarbeiterDaten: {
        employmentPermitMetaInfo: {
          missingSectionsInLastMail: { sectionID: 1; sectionName: 1; reason: 1 };
          lastEmailSentAt: 1;
          initialEmailSentAt: 1;
        };
      };
    }
  >,
  existingPermits: boolean,
): {
  status: Renderable;
  colorBorder: string;
  colorBackground: string;
} {
  const employmentPermitMetaInfo = person.mitarbeiterDaten?.employmentPermitMetaInfo;
  // Saving employee data used to write an empty meta info for everyone, so its mere presence says
  // nothing — only a send date means an application went out.
  if (!employmentPermitMetaInfo?.lastEmailSentAt) {
    return {
      status: html`
        <div class="flex flex-col gap-2">
          <h4 class="font-semibold">Noch kein Antrag über die Plattform versendet.</h4>
          <p>
            ${
              existingPermits
                ? 'Die vorhandenen Beschäftigungsgenehmigungen wurden außerhalb der Plattform beantragt.'
                : 'Für diese Person wurde noch kein Antrag auf Beschäftigungsgenehmigung erstellt.'
            }
            Mit „Antrag manuell generieren“ wird der Antrag mit den hinterlegten Anlagen erstellt und per E-Mail
            versendet.
          </p>
        </div>
      `,
      colorBorder: 'border-primary',
      colorBackground: 'bg-primary/30',
    };
  }

  if (!employmentPermitMetaInfo.missingSectionsInLastMail?.length)
    return {
      status: html`
        <div class="flex flex-col gap-2">
          <h4 class="font-semibold">Antrag vollständig versendet.</h4>
          <p>Der letzte Versand hat alle benötigten Anlagen enthalten.</p>
        </div>
      `,
      colorBorder: 'border-success',
      colorBackground: 'bg-success/30',
    };

  return {
    status: html`
      <div class="flex flex-col gap-2">
        <h4 class="font-semibold">Antrag nicht vollständig versendet.</h4>
        <p>Bitte stellen Sie die fehlenden Dateien und Templates zur Verfügung und versenden den Antrag nochmals.</p>
      </div>
    `,
    colorBorder: 'border-warning',
    colorBackground: 'bg-warning/30',
  };
}

function showDates(
  person: NarrowedEntity<
    Person,
    { mitarbeiterDaten: { employmentPermitMetaInfo: { lastEmailSentAt: 1; initialEmailSentAt: 1 } } }
  >,
) {
  const dates: { label: string; date: Maybe<Date> }[] = [
    {
      label: 'Automatische Versendung',
      date: person.mitarbeiterDaten?.employmentPermitMetaInfo?.initialEmailSentAt,
    },
    {
      label: 'Zuletzt manuell generiert',
      date: person.mitarbeiterDaten?.employmentPermitMetaInfo?.lastEmailSentAt,
    },
    // we sort so we get the non nullish values first and the grid aligns with empty divs
  ].sort((a, b) => (a.date ? -1 : 1));

  return html`
    <div class="grid grid-cols-[max-content_max-content] items-center gap-3">
      ${dates.map(date =>
        date.date
          ? html` <span>${date.label}: </span>
              <span class="font-bold"> ${format(date.date, 'dd.MM.yyyy HH:mm')} </span>`
          : html`<div></div>
              <div></div>`,
      )}
    </div>
  `;
}

export const SGEmploymentPermitStatus = stateful((props: { userID: Maybe<string>; existingPermits: boolean }) => {
  const user = useObservable(() => currentUser<Person>(), []);

  // TODO make new permission for that?
  if (!user?.hasPermission(Permission.AG_SEKTION_R)) return nothing;

  const { value: person, loading: personLoading } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.userID,
      )({
        activeContract: { _id: 1 },
        mitarbeiterDaten: {
          employmentPermitMetaInfo: {
            missingSectionsInLastMail: {
              sectionID: 1,
              sectionName: 1,
              reason: 1,
            },
            lastEmailSentAt: 1,
            initialEmailSentAt: 1,
          },
        },
      }),
    [props.userID],
  );

  if (!person || personLoading) return nothing;

  const status = getStatus(person, props.existingPermits);

  const missingSections = person.mitarbeiterDaten?.employmentPermitMetaInfo?.missingSectionsInLastMail ?? [];

  return html`
    <div class="card flex flex-grow p-4 gap-4 justify-center border-2 ${status.colorBorder} ${status.colorBackground}">
      <div class="flex flex-row justify-between gap-4">${status.status} ${showDates(person)}</div>

      ${
        missingSections.length
          ? html`
              <div class="card mt-2 p-4 gap-4">
                <p>Folgende Anlagen fehlen:</p>
                <div class="grid grid-cols-[max-content_max-content_1fr] items-center gap-2">
                  ${repeat(
                    missingSections,
                    section => section.sectionID,
                    (section, index) => html`
                      <span class="badge badge-ghost justify-self-start">Anlage: <b>${section.sectionID}</b></span>
                      <span>${section.sectionName}</span>

                      <span class="text-sm break-words text-base-content/70">${section.reason}</span>
                      ${
                        index < missingSections.length - 1
                          ? html`<div class="col-span-3 h-1 bg-base-200 rounded-2xl"></div>`
                          : nothing
                      }
                    `,
                  )}
                </div>
              </div>
            `
          : nothing
      }
      <div class="flex flex-row">
        ${
          user.hasPermission(Permission.AG_SEKTION_W)
            ? html`<button
                class="btn btn-primary"
                @click=${async () => {
                  const confirm = await confirmDialog('Sind Sie sicher, dass Sie den Antrag versenden möchten?');
                  if (!confirm) return;
                  await guardDialog(
                    () => {
                      if (!person.activeContract) {
                        throw new Error('Kein aktiver Vertrag gefunden');
                      }
                      return EmploymentPermitsAPI.sendBGADDMail(person.activeContract._id)();
                    },
                    {
                      success: () => {
                        void SgSnackbar.show('Antrag erfolgreich versendet');
                      },
                    },
                  );
                }}
              >
                Antrag manuell generieren
              </button>`
            : nothing
        }
      </div>
    </div>
  `;
});
